<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from "vue-toastification";
import { bookletApi } from "@/Api/booklet-api";
import { useNavigationStore } from "@/Stores/navigationStore";
import { useUserStore } from "@/Stores/userStore";
import { useUniversalChatStore } from "@/Stores/universalChatStore";
import NavigationSection from "./NavigationSection.vue";
import AddBookletForm from "@/Components/GenerationForms/AddBookletForm.vue";

const router = useRouter();
const toast = useToast();
const navigationStore = useNavigationStore();
const userStore = useUserStore();
const chatStore = useUniversalChatStore();

const isLoading = ref(false);
const showCreateBookletModal = ref(false);
const selectedCategory = ref(null);
const collections = ref({
    creatures: [],
    characters: [],
    merchants: [],
});

const sections = [
    { key: 'creatures', title: 'Creatures', singular: 'Creature', icon: ['far', 'paw-claws'], generatePath: '/creature#generate' },
    { key: 'characters', title: 'Characters', singular: 'Character', icon: ['far', 'sword'], generatePath: '/character#generate' },
    { key: 'merchants', title: 'Merchants', singular: 'Merchant', icon: ['fad', 'sack'], generatePath: '/merchant#generate' },
];

const isLoggedIn = () => userStore.userId != null;

function fetchRecentlyUsedCollections() {
    if (!isLoggedIn()) {
        isLoading.value = false;
        return;
    }
    isLoading.value = true;
    bookletApi.getRecentlyUsedCollections().then(response => {
        collections.value = response.data;
        isLoading.value = false;
    }).catch(error => {
        isLoading.value = false;
    });
}

function handleNavigate(payload) {
    if (payload.type === 'collection') {
        router.push({ path: `/collections/${payload.id}` });
    } else if (payload.type === 'viewAllCollections') {
        router.push({ path: '/collections', query: { type: payload.categoryKey } });
    } else if (payload.type === 'generate') {
        const [path, hash] = payload.generatePath.split('#');
        router.push({ path, hash: hash ? `#${hash}` : undefined });
    }
    navigationStore.closeLeftPanel();
}

function handleCreateCollection(category) {
    if (!isLoggedIn()) {
        navigationStore.closeLeftPanel();
        chatStore.requestAuthModal('registration');
        return;
    }
    selectedCategory.value = category;
    navigationStore.closeLeftPanel();
    showCreateBookletModal.value = true;
}

function handleBookletCreated() {
    showCreateBookletModal.value = false;
    selectedCategory.value = null;
    fetchRecentlyUsedCollections();
}

function closePanel() {
    navigationStore.closeLeftPanel();
}

function handleKeydown(e) {
    if (e.key === 'Escape' && navigationStore.isLeftPanelOpen) {
        closePanel();
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
});

watch(() => navigationStore.isLeftPanelOpen, (isOpen) => {
    if (isOpen) {
        fetchRecentlyUsedCollections();
        document.body.style.overflow = 'hidden';
    } else {
        document.body.style.overflow = '';
    }
});
</script>

<template>
    <teleport to="body">
        <transition
            enter-active-class="transition-opacity duration-300"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-show="navigationStore.isLeftPanelOpen"
                class="fixed inset-0 bg-black bg-opacity-50 z-40"
                @click="closePanel"
            />
        </transition>

        <transition
            enter-active-class="transition-transform duration-300 ease-out"
            enter-from-class="-translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transition-transform duration-200 ease-in"
            leave-from-class="translate-x-0"
            leave-to-class="-translate-x-full"
        >
            <div
                v-show="navigationStore.isLeftPanelOpen"
                class="fixed top-0 left-0 h-full w-80 bg-gray-900 border-r border-gray-700 z-50 flex flex-col"
            >
                <div class="flex items-center px-4 py-3 border-b border-gray-700">
                    <router-link to="/" @click="closePanel" class="flex items-center">
                        <img src="/api/images/PressKit/logo_white.webp" alt="Tavern of Azoth" class="h-12" />
                    </router-link>
                </div>

                <div class="flex-1 overflow-y-auto">
                    <NavigationSection
                        v-for="section in sections"
                        :key="section.key"
                        :title="section.title"
                        :singular="section.singular"
                        :icon="section.icon"
                        :category-key="section.key"
                        :generate-path="section.generatePath"
                        :collections="collections[section.key]"
                        :loading="isLoading"
                        @navigate="handleNavigate"
                        @create="handleCreateCollection"
                    />
                </div>
            </div>
        </transition>
    </teleport>

    <AddBookletForm
        :show="showCreateBookletModal"
        :category="selectedCategory"
        @close="showCreateBookletModal = false; selectedCategory = null"
        @created="handleBookletCreated"
    />
</template>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: #1F2937;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background: #4B5563;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #6B7280;
}
</style>
